import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  MenuItem,
  Select,
  FormControl,
} from "@mui/material";
import { styled, useTheme as useMuiTheme } from "@mui/material/styles";
import api from "../../utilities/api";
// import CloudIcon from "@mui/icons-material/Cloud";
// import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";

import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import TrendingDownOutlinedIcon from "@mui/icons-material/TrendingDownOutlined";
import ReactApexChart from "react-apexcharts";

// -- Styled Components --
const StyledCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.default,
  boxShadow: theme.shadows[1],
  borderRadius: theme.shape.borderRadius,
  display: "flex",
  flexDirection: "column",
  height: "100%",
}));

const StatBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.default,
  display: "flex",
  flexDirection: "column",
  color: theme.palette.text.primary,
  height: "100%",
}));

const IconWrapper = styled(Box)(({ theme, bgcolor }) => ({
  backgroundColor: bgcolor,
  borderRadius: "50%",
  width: 32,
  height: 32,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

// Styled trend icons with appropriate colors
const TrendingUpIcon = styled(TrendingUpOutlinedIcon)(({ theme }) => ({
  color: theme.palette.success.main,
  fontSize: 20,
}));

const TrendingDownIcon = styled(TrendingDownOutlinedIcon)(({ theme }) => ({
  color: theme.palette.error.main,
  fontSize: 20,
}));

// -- BarChart (Top Storage Consumers) --
const BarChart = ({ data }) => {
  const theme = useMuiTheme();

  const categories = data ? data.map((d) => d.name) : [];
  const seriesData = data ? data.map((d) => d.usedSpaceGB) : [];

  const state = {
    series: [
      {
        name: "Storage Used",
        data: seriesData, // in GB
      },
    ],
    options: {
      chart: {
        type: "bar",
        height: "100%",
        background: "transparent",
        toolbar: { show: false },
      },
      plotOptions: {
        bar: {
          horizontal: true,
          borderRadius: 4,
          barHeight: "60%",
        },
      },
      dataLabels: { enabled: false },
      xaxis: {
        categories: categories,
        labels: {
          style: {
            fontSize: "14px",
            fontWeight: "500",
            colors: theme.palette.text.primary,
          },
          formatter: (val) => `${val}GB`,
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: {
        labels: {
          style: {
            fontSize: "14px",
            fontWeight: "500",
            colors: theme.palette.text.primary,
          },
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      grid: { show: false },
      tooltip: {
        y: {
          formatter: (val) => `${val} GB`,
        },
        theme: theme.palette.mode,
      },
    },
  };

  return (
    <ReactApexChart
      options={state.options}
      series={state.series}
      type="bar"
      height={450}
    />
  );
};

// -- PieChart (Top Products Used) --
const PieChart = ({ data }) => {
  const theme = useMuiTheme();

  // Data processing
  const labels =
    data && data.length > 0 ? data.map((d) => d.name) : ["No Data"];
  const series =
    data && data.length > 0 ? data.map((d) => parseFloat(d.percentage)) : [100];
  // Generate random colors or use a palette
  const colors = [
    "#1E90FF",
    "#FF4136",
    "#FFB900",
    "#00C1D4",
    "#32CD32",
    "#8A2BE2",
    "#FF1493",
  ];

  // Custom legend component that adapts to light/dark mode
  const renderCustomLegend = () => {
    if (!data || data.length === 0)
      return <Typography>No usage data available</Typography>;

    return (
      <Box sx={{ pl: 2 }}>
        {data.map((item, index) => (
          <Box
            key={index}
            sx={{ display: "flex", alignItems: "center", mb: 1 }}
          >
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                backgroundColor: colors[index % colors.length],
                mr: 1.5,
              }}
            />
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 500,
                  color:
                    theme.palette.mode === "dark"
                      ? "#fff"
                      : theme.palette.text.primary,
                  width: "120px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={item.name}
              >
                {item.name}
              </Typography>
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 500,
                  color:
                    theme.palette.mode === "dark"
                      ? "#fff"
                      : theme.palette.text.primary,
                  width: "60px",
                }}
              >
                {parseFloat(item.percentage).toFixed(1)}%
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  const state = {
    series: series,
    options: {
      chart: {
        type: "pie",
        background: "transparent",
      },
      labels: labels,
      colors: colors,
      legend: {
        show: false, // Hide default legend, we'll use our custom one
      },
      tooltip: {
        y: { formatter: (val) => `${val}%` },
        theme: theme.palette.mode,
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        width: 0,
      },
    },
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "row" }}>
      <Box sx={{ width: "50%" }}>
        <ReactApexChart
          options={state.options}
          series={state.series}
          type="pie"
          width="100%"
          height={300}
        />
      </Box>
      <Box sx={{ width: "50%", display: "flex", alignItems: "center" }}>
        {renderCustomLegend()}
      </Box>
    </Box>
  );
};

// -- Main Component --
export default function StorageUsageDashboard() {
  const theme = useMuiTheme();
  const [timeFilter, setTimeFilter] = useState("This year");
  const [stats, setStats] = useState({
    totalCapacity: "1TB",
    totalUsed: "0GB",
    usedPercentage: 0,
    topConsumers: [],
    overview: null,
    fileTypes: [],
    userStats: null,
  });
  const [loading, setLoading] = useState(true);
  const [appUsage, setAppUsage] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const statsRes = await api.get("/api/storage/stats");
        if (statsRes.data.success) {
          setStats(statsRes.data.data);
        }

        const appsRes = await api.get("/api/storage/apps");
        if (appsRes.data.success) {
          setAppUsage(appsRes.data.data);
        }
      } catch (err) {
        console.error("Error fetching storage data", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Typography>Loading dashboard...</Typography>
      </Box>
    );
  }

  return (
    <Box
      maxWidth="100%"
      sx={{
        bgcolor: theme.palette.background.default,
        p: 0,
        m: 0,
        border: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(2),
        borderRadius: 2,
      }}
    >
      {/* Overview Stats */}
      {stats.overview && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" fontWeight="bold" sx={{ mb: 2 }}>
            Storage Overview
          </Typography>
          <Grid container spacing={2}>
            {[
              {
                label: "Total Files",
                value: stats.overview.files,
                color: "#2196f3",
              },
              {
                label: "Total Folders",
                value: stats.overview.folders,
                color: "#ff9800",
              },
              {
                label: "Trashed Items",
                value: stats.overview.trashedItems,
                color: "#f44336",
              },
              {
                label: "Trash Size",
                value: stats.overview.trashSize,
                color: "#d32f2f",
              },
              {
                label: "Starred Items",
                value: stats.overview.starredItems,
                color: "#ffeb3b",
              },
              {
                label: "Shared Items",
                value: stats.overview.sharedItems,
                color: "#4caf50",
              },
            ].map((item, index) => (
              <Grid item xs={6} sm={4} md={2} key={index}>
                <StyledCard
                  sx={{
                    alignItems: "center",
                    justifyContent: "center",
                    p: 2,
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Typography variant="h4" fontWeight="bold" color={item.color}>
                    {item.value}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {item.label}
                  </Typography>
                </StyledCard>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Top Stats Bar */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 3,
          mb: 3,
        }}
        className="top-stats-bar"
      >
        {/* Stat 1 */}
        <Box
          sx={{
            flex: "1 1 300px",
          }}
        >
          <StatBox
            sx={{
              boxShadow: "none",
              border: `1px solid ${theme.palette.divider}`,
              p: 2,
              borderRadius: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box>
                <Typography
                  variant="caption"
                  color={theme.palette.text.secondary}
                  fontSize="12px"
                >
                  Total Storage Capacity
                </Typography>
                <Typography variant="h6" fontWeight="bold" fontSize="24px">
                  {stats.totalCapacity}
                </Typography>
              </Box>
              <IconWrapper bgcolor="#25A619" sx={{ borderRadius: "5px" }}>
                <Box
                  component="img"
                  src="/images/typcn_cloud-storage-outline.png"
                  alt="Storage Icon"
                  sx={{ width: 32, height: 32, objectFit: "contain" }}
                />
              </IconWrapper>
            </Box>
          </StatBox>
        </Box>

        {/* Stat 2 */}
        <Box sx={{ flex: "1 1 300px" }}>
          <StatBox
            sx={{
              boxShadow: "none",
              border: `1px solid ${theme.palette.divider}`,
              p: 2,
              borderRadius: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box>
                <Typography
                  variant="caption"
                  color={theme.palette.text.secondary}
                  fontSize="12px"
                >
                  Total Used Storage
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
                  <Typography>{stats.totalUsed}</Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      ml: 1,
                      p: 1,
                      borderRadius: "24px",
                      border: `1px solid ${theme.palette.success.main}`,
                    }}
                  >
                    <TrendingUpIcon sx={{ width: 30, height: 20 }} />
                    <Typography sx={{ ml: 1 }}>
                      {stats.usedPercentage}%
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <IconWrapper bgcolor="#1976d2" sx={{ borderRadius: "5px" }}>
                <Box
                  component="img"
                  src="/images/Group 2928.png"
                  alt="File Icon"
                  sx={{ width: 24, height: 24, objectFit: "contain" }}
                />
              </IconWrapper>
            </Box>
          </StatBox>
        </Box>

        {/* Stat 3 */}
        <Box sx={{ flex: "1 1 300px" }}>
          <StatBox
            sx={{
              boxShadow: "none",
              border: `1px solid ${theme.palette.divider}`,
              p: 2,
              borderRadius: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box>
                <Typography
                  variant="caption"
                  color={theme.palette.text.secondary}
                  fontSize="12px"
                >
                  Active Users
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
                  <Typography>
                    {stats.userStats ? stats.userStats.activeUsers : 0} /{" "}
                    {stats.userStats ? stats.userStats.totalUsers : 0}
                  </Typography>
                </Box>
                <Typography variant="caption" color="textSecondary">
                  Avg Storage:{" "}
                  {stats.userStats ? stats.userStats.avgStorageUsed : "0 B"}
                </Typography>
              </Box>
              <IconWrapper bgcolor="#1976d2" sx={{ borderRadius: "2px" }}>
                <Box
                  component="img"
                  src="/images/ph_cube-fill.png"
                  alt="Cube Icon"
                  sx={{
                    width: 24,
                    height: 24,
                    objectFit: "contain",
                    borderRadius: "5px",
                  }}
                />
              </IconWrapper>
            </Box>
          </StatBox>
        </Box>
      </Box>

      {/* File Types Section */}
      {stats.fileTypes && stats.fileTypes.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2}>
            {stats.fileTypes.map((type, index) => (
              <Grid item xs={12} sm={6} md={2} key={index}>
                <StyledCard
                  sx={{
                    alignItems: "center",
                    justifyContent: "center",
                    p: 2,
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Typography variant="subtitle2" noWrap title={type.type}>
                    {type.type}
                  </Typography>
                  <Typography variant="h6">{type.count}</Typography>
                  <Typography variant="caption" color="textSecondary">
                    {type.sizeFormatted}
                  </Typography>
                </StyledCard>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          justifyContent: "space-between",
        }}
      >
        {/* Top Consumers */}
        <Box
          sx={{
            flexBasis: { xs: "100%", md: "48%" },
            boxShadow: "none",
            border: `1px solid ${theme.palette.divider}`,
            p: 2,
            borderRadius: 2,
          }}
        >
          <StyledCard>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}
            >
              <Typography variant="h6">Top Storage Consumers</Typography>
            </Box>
            <BarChart data={stats.topConsumers} />
          </StyledCard>
        </Box>

        {/* Top Products Used */}
        <Box
          sx={{
            flexBasis: { xs: "100%", md: "48%" },
            boxShadow: "none",
            border: `1px solid ${theme.palette.divider}`,
            p: 2,
            borderRadius: 2,
          }}
        >
          <StyledCard>
            <Typography variant="h6" mb={2}>
              Top Products Used
            </Typography>
            <PieChart data={appUsage} />
          </StyledCard>
        </Box>
      </Box>
    </Box>
  );
}
